import { Skeleton } from "@/components/ui/skeleton";
import { ChartCard } from "./chart-card";
import { HeatmapLegend, ReceptionHeatmapGrid, activeDays, activeSlots } from "./reception-heatmap";
import type { ReceptionTimelineApprover } from "@/lib/statistics-api.service";

interface ApproverHeatmapsProps {
	/** `undefined` = lần tải đầu chưa xong, khác mảng rỗng (đã tải, không ai tiếp nhận). */
	approvers: ReceptionTimelineApprover[] | undefined;
	/** Rỗng nghĩa là KHÔNG lọc — hiện mọi người, giống "Tất cả khoa" ở các bộ lọc khác. */
	selectedIds: string[];
}

/**
 * Một lưới nhiệt độ nhỏ cho mỗi người tiếp nhận, xếp dưới lưới tổng.
 *
 * Trục ngày, trục ô 30 phút và thang màu đều tính từ TOÀN BỘ người đang hiện
 * rồi ép xuống từng lưới, không để mỗi lưới tự co theo dữ liệu riêng: các lưới
 * nhỏ chỉ có nghĩa khi so sánh được với nhau, mà tự co trục thì hai ô cùng vị
 * trí ở hai lưới lại là hai khung giờ khác nhau.
 */
export function ApproverHeatmaps({ approvers, selectedIds }: ApproverHeatmapsProps) {
	if (approvers === undefined) {
		return (
			<ChartCard
				title="Nhiệt độ theo từng người tiếp nhận"
				filename="nhiet-do-tung-nguoi-tiep-nhan"
			>
				<div className="space-y-4">
					{Array.from({ length: 3 }).map((_, i) => (
						<Skeleton key={i} className="h-24 w-full" />
					))}
				</div>
			</ChartCard>
		);
	}

	const selected = new Set(selectedIds);
	const shown =
		selected.size === 0 ? approvers : approvers.filter((a) => selected.has(a.approverId));

	const allBuckets = shown.flatMap((a) => a.buckets);
	const days = activeDays(allBuckets);
	const slots = activeSlots(allBuckets);
	const maxCount = Math.max(1, ...allBuckets.map((b) => b.count));

	return (
		<ChartCard
			title="Nhiệt độ theo từng người tiếp nhận"
			filename="nhiet-do-tung-nguoi-tiep-nhan"
			description="Cùng trục ngày/giờ và cùng thang màu với nhau, giờ Việt Nam (UTC+7)"
		>
			{shown.length === 0 || slots.length === 0 ? (
				<p className="py-8 text-center text-sm text-muted-foreground">
					{approvers.length === 0
						? "Chưa quy được hồ sơ nào cho người tiếp nhận trong phạm vi đang lọc."
						: "Không có người tiếp nhận nào khớp lựa chọn hiện tại."}
				</p>
			) : (
				<>
					<div className="space-y-5">
						{shown.map((approver) => (
							<div key={approver.approverId}>
								<div className="mb-1.5 flex items-baseline justify-between gap-2">
									<p className="truncate text-sm font-medium">{approver.approverName}</p>
									<p className="shrink-0 text-xs text-muted-foreground">
										{approver.totalReceived.toLocaleString("vi-VN")} hồ sơ
									</p>
								</div>
								<ReceptionHeatmapGrid
									buckets={approver.buckets}
									days={days}
									slots={slots}
									maxCount={maxCount}
									dayLabelWidth="3.5rem"
								/>
							</div>
						))}
					</div>
					<HeatmapLegend maxCount={maxCount} />
				</>
			)}
		</ChartCard>
	);
}
